// lib/koi-pond-v3/quality/tiers.ts
//
// Device quality tiers for the v3 pond. The fluidity audit established that the
// scene is GPU-fragment-bound (the DoF pass + the full-screen procedural water
// /caustic shaders), not CPU/JS-bound — so the tier dials are mostly fragment
// cost: DPR, post-FX (DoF), MSAA, full-screen shader detail, and SSS, plus koi
// count (cheap by comparison) and particle pools.
//
// A tier is chosen at load from device signals (DPR, screen size,
// hardwareConcurrency, deviceMemory) with a `?tier=` override for testing, then
// a short runtime warmup can apply "relief" (drop DoF + DPR) if the first frames
// overrun the 60fps budget — targeting the proven bottleneck without a costly
// full remount.

export type TierId = 'premium' | 'standard' | 'lite';

export interface TierBudget {
  id: TierId;
  /** devicePixelRatio cap. */
  dprCap: number;
  /** WebGLRenderer MSAA for the default framebuffer. */
  antialias: boolean;
  /** Multisample count for the EffectComposer render targets (0 = off). */
  composerSamples: number;
  /** Depth-of-field post pass. */
  dof: boolean;
  /** Resident koi count. */
  koiCount: number;
  /** Koi geometry tessellation factor (1 = default desktop). */
  koiQuality: number;
  /** Subsurface scattering in the koi material. */
  sss: boolean;
  /** Caustic floor detail multiplier (>1 adds an extra octave on premium). */
  causticDetail: number;
  /** Firefly count. */
  fireflies: number;
}

export const TIERS: Record<TierId, TierBudget> = {
  premium: {
    id: 'premium',
    dprCap: 2,
    antialias: true,
    composerSamples: 4, // MSAA on the water/scene through the composer
    dof: true,
    koiCount: 9,
    koiQuality: 1.35,
    sss: true,
    causticDetail: 1.5,
    fireflies: 7,
  },
  standard: {
    id: 'standard',
    dprCap: 2,
    antialias: true,
    composerSamples: 0,
    dof: true,
    koiCount: 3,
    koiQuality: 1,
    sss: true,
    causticDetail: 1,
    fireflies: 5,
  },
  lite: {
    id: 'lite',
    dprCap: 1.5,
    antialias: false,
    composerSamples: 0,
    dof: false,
    koiCount: 3,
    koiQuality: 0.6,
    sss: false,
    causticDetail: 1,
    fireflies: 3,
  },
};

const ORDER: TierId[] = ['lite', 'standard', 'premium'];

/** One step down the ladder (premium→standard→lite); lite stays lite. */
export function lowerTier(id: TierId): TierId {
  const i = ORDER.indexOf(id);
  return ORDER[Math.max(0, i - 1)];
}

/**
 * Pick a tier from device signals. Honors a `?tier=premium|standard|lite`
 * query override (for testing + owner A/B). Runtime warmup relief is applied
 * separately in the scene.
 */
export function detectTier(): TierId {
  if (typeof window === 'undefined') return 'standard';

  const override = new URLSearchParams(window.location.search).get('tier');
  if (override === 'premium' || override === 'standard' || override === 'lite') {
    return override;
  }

  const coarse =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const w = window.innerWidth;
  if (w < 768 || coarse) return 'lite';

  const cores = navigator.hardwareConcurrency || 4;
  // deviceMemory is Chromium-only and may be undefined; treat unknown as 4GB.
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;

  if (cores >= 8 && mem >= 8 && w >= 1280) return 'premium';
  return 'standard';
}
